import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Tag } from 'antd';
import {
  ClipboardText,
  ClockCounterClockwise,
  NotePencil,
} from '@phosphor-icons/react';
import Typography from '../../../components/Typography';
import Button from '../../../components/Button';
import COLORS from '../../../util/constants/colors';
import {
  changedFollowUpFields,
  selectOptions,
} from './callFollowUpFormUtils';
import useCallFollowUpEditor from './useCallFollowUpEditor';
import './callFollowUps.scss';
import {
  AddEntryForm,
  FollowUpOwnerField,
  FollowUpFormFields,
  formatEntryCreator,
  formatEntryDate,
} from './ExistingFollowUpEditorPrimitives';

const followUpCountText = (count) => `${count} follow-up${count === 1 ? '' : 's'}`;

const DrawerExistingFollowUpEditorView = ({
  followup,
  saving,
  saveError,
  editForm,
  entryForm,
  setEntryForm,
  assigneeOptions,
  statusOptions,
  dispositionOptions,
  controlsDisabled,
  detailsDirty,
  showAddEntry,
  setShowAddEntry,
  cancelEdit,
  updateDetails,
  updateEditFormField,
  addEntry,
}) => {
  const renderWorkflowHeader = () => {
    const entryCount = followup.entries?.length || 0;
    const completed = !!followup.completed || !!followup.completed_at;

    return (
      <div className="follow-up-workflow-header">
        <div className="follow-up-detail-section-title-with-icon">
          <ClipboardText size={18} weight="fill" />
          <Typography className="follow-up-detail-section-title" variant="text" size="small" bold>Follow Up</Typography>
        </div>
        <div className="follow-up-workflow-badges">
          <span className="follow-up-count-badge">{followUpCountText(entryCount)}</span>
          <span className={`follow-up-state-badge ${completed ? 'done' : 'open'}`}>
            {completed ? 'Done' : 'Open'}
          </span>
        </div>
      </div>
    );
  };

  const renderEditControls = () => (
    <>
      <div className="follow-up-workflow-fields">
        <FollowUpOwnerField
          followup={followup}
          className="follow-up-workflow-field"
          labelClassName="follow-up-workflow-field-label"
        />
      </div>
      <FollowUpFormFields
        form={editForm}
        formGridClassName="follow-up-detail-form-grid"
        inputClassName="follow-up-detail-input"
        assigneeOptions={assigneeOptions}
        dispositionOptions={dispositionOptions}
        controlsDisabled={controlsDisabled}
        updateField={updateEditFormField}
      />
      {detailsDirty && (
        <div className="follow-up-detail-actions follow-up-workflow-actions follow-up-detail-save-actions">
          <Button type="secondary" size="xsmall" label="Cancel" onClick={cancelEdit} disabled={saving} />
          <Button type="primary" size="xsmall" label="Save changes" onClick={updateDetails} disabled={controlsDisabled} />
        </div>
      )}
    </>
  );

  const renderEntry = (entry, index) => (
    <div className="follow-up-detail-entry" key={entry.id}>
      <div className="follow-up-detail-entry-content">
        <div className="follow-up-detail-entry-header">
          <div className="follow-up-detail-entry-title-row">
            <Typography className="follow-up-detail-entry-title" variant="text" size="small" bold>
              {`Follow Up ${entry.position || index + 1}`}
            </Typography>
            <Tag className="follow-up-entry-status-tag">{entry.call_followup_status?.name || 'Status'}</Tag>
          </div>
          <Typography variant="text" size="xsmall" color={COLORS.TEXT_TERTIARY_LITE}>
            {`${formatEntryCreator(entry)}${formatEntryDate(entry.created_at) ? ` - ${formatEntryDate(entry.created_at)}` : ''}`}
          </Typography>
        </div>
        {entry.note && <Typography className="follow-up-detail-entry-note" variant="text" size="small">{entry.note}</Typography>}
      </div>
    </div>
  );

  const renderAddEntryControls = () => {
    if (!showAddEntry) {
      return (
        <div className="follow-up-detail-add-entry-trigger">
          <Button
            type="primary"
            size="xsmall"
            label="Add Follow Up"
            leftIcon={<NotePencil size={16} />}
            onClick={() => setShowAddEntry(true)}
            disabled={controlsDisabled}
          />
        </div>
      );
    }

    return (
      <AddEntryForm
        addEntryClassName="follow-up-detail-add-entry"
        actionsClassName="follow-up-detail-actions follow-up-workflow-actions"
        inputClassName="follow-up-detail-input"
        noteInputClassName="follow-up-detail-input follow-up-detail-note"
        entryForm={entryForm}
        setEntryForm={setEntryForm}
        statusOptions={statusOptions}
        controlsDisabled={controlsDisabled}
        saving={saving}
        showCancel
        submitLabel="Add Follow Up"
        submitSize="xsmall"
        onCancel={() => setShowAddEntry(false)}
        onSubmit={addEntry}
      />
    );
  };

  return (
    <div className="follow-up-workflow-card" data-testid="call-followup-detail">
      {saveError && <Alert message={saveError} type="error" showIcon />}
      {renderWorkflowHeader()}
      {renderEditControls()}
      {renderAddEntryControls()}
      <div className="follow-up-detail-entries-section">
        <div className="follow-up-detail-section-title-with-icon follow-up-history-title">
          <ClockCounterClockwise size={18} weight="fill" />
          <Typography className="follow-up-detail-section-title" variant="text" size="small" bold>Follow Up History</Typography>
        </div>
        <div className="follow-up-detail-entry-list">
          {(followup.entries || []).length === 0 ? (
            <Typography className="follow-up-detail-empty-history" variant="text" size="small" color={COLORS.TEXT_TERTIARY_LITE}>
              No follow-ups yet. Add the first follow-up to start working this call.
            </Typography>
          ) : (followup.entries || []).map(renderEntry)}
        </div>
      </div>
    </div>
  );
};

const DrawerExistingFollowUpEditor = ({
  followup,
  options,
  optionsLoading,
  optionsError,
  onFollowUpChanged,
}) => {
  const editor = useCallFollowUpEditor({ followup, onFollowUpChanged, analyticsSurface: 'voice_followups_drawer' });
  const [showAddEntry, setShowAddEntry] = useState(false);
  const assigneeOptions = useMemo(
    () => selectOptions(options?.assignees, (assignee) => assignee.name),
    [options],
  );
  const statusOptions = useMemo(() => selectOptions(options?.call_followup_statuses), [options]);
  const dispositionOptions = useMemo(() => selectOptions(options?.call_dispositions), [options]);
  const controlsDisabled = editor.saving || optionsLoading || !!optionsError || !options;
  const detailsDirty = Object.keys(changedFollowUpFields(editor.editForm, followup)).length > 0;

  useEffect(() => {
    const firstStatusId = options?.call_followup_statuses?.[0]?.id || null;
    if (!firstStatusId || editor.entryForm.statusId) return;

    editor.setEntryForm((form) => ({ ...form, statusId: firstStatusId }));
  }, [options?.call_followup_statuses, editor.entryForm.statusId, editor.setEntryForm]);

  const updateEditFormField = (field, value) => {
    editor.setEditForm({ ...editor.editForm, [field]: value });
  };

  const addEntry = async () => {
    const saved = await editor.addEntry();
    if (saved) setShowAddEntry(false);
    return saved;
  };

  return (
    <DrawerExistingFollowUpEditorView
      followup={followup}
      saving={editor.saving}
      saveError={editor.saveError}
      editForm={editor.editForm}
      entryForm={editor.entryForm}
      setEntryForm={editor.setEntryForm}
      assigneeOptions={assigneeOptions}
      statusOptions={statusOptions}
      dispositionOptions={dispositionOptions}
      controlsDisabled={controlsDisabled}
      detailsDirty={detailsDirty}
      showAddEntry={showAddEntry}
      setShowAddEntry={setShowAddEntry}
      cancelEdit={editor.cancelEdit}
      updateDetails={editor.updateDetails}
      updateEditFormField={updateEditFormField}
      addEntry={addEntry}
    />
  );
};

DrawerExistingFollowUpEditorView.propTypes = {
  followup: PropTypes.object.isRequired,
  saving: PropTypes.bool.isRequired,
  saveError: PropTypes.string,
  editForm: PropTypes.object.isRequired,
  entryForm: PropTypes.object.isRequired,
  setEntryForm: PropTypes.func.isRequired,
  assigneeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  statusOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  dispositionOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  controlsDisabled: PropTypes.bool.isRequired,
  detailsDirty: PropTypes.bool.isRequired,
  showAddEntry: PropTypes.bool.isRequired,
  setShowAddEntry: PropTypes.func.isRequired,
  cancelEdit: PropTypes.func.isRequired,
  updateDetails: PropTypes.func.isRequired,
  updateEditFormField: PropTypes.func.isRequired,
  addEntry: PropTypes.func.isRequired,
};

DrawerExistingFollowUpEditorView.defaultProps = {
  saveError: null,
};

DrawerExistingFollowUpEditor.propTypes = {
  followup: PropTypes.object.isRequired,
  options: PropTypes.object,
  optionsLoading: PropTypes.bool,
  optionsError: PropTypes.string,
  onFollowUpChanged: PropTypes.func,
};

DrawerExistingFollowUpEditor.defaultProps = {
  options: null,
  optionsLoading: false,
  optionsError: null,
  onFollowUpChanged: () => {},
};

export default DrawerExistingFollowUpEditor;
